import { UserNotification } from '@/_shared/models/user-notification.model';
import { UserNotificationService } from '@/_shared/services';
import container from '@/_shared/services/service-container';
import { Bell } from 'lucide-react';
import moment from 'moment';
import { useRouter } from 'next/router';
import { useState, useRef, useEffect, useCallback } from 'react';

const NotiPanel = () => {
  const [notificationList, setNotificationList] =
    useState<UserNotification[]>();
  const [notiCount, setNotiCount] = useState<number>(0);
  const notiService = container.get<UserNotificationService>(
    'UserNotificationService'
  );

  const [panelOpen, setPanelOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellIconRef = useRef<HTMLButtonElement>(null);

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      bellIconRef.current &&
      !bellIconRef.current.contains(event.target as Node)
    ) {
      setPanelOpen(false);
      document.removeEventListener('mousedown', handleClickOutside);
    }
  };

  const togglePanel = () => {
    if (panelOpen) {
      document.removeEventListener('mousedown', handleClickOutside);
    } else {
      document.addEventListener('mousedown', handleClickOutside);
    }
    setPanelOpen(!panelOpen);
  };

  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const getNotificationList = async () => {
      const resp = await notiService.getUserNotification();
      setNotificationList(resp.data?.result || []);
      setLoading(false);
    };

    if (loading) {
      getNotificationList();
    }
  }, [notiService, loading]);

  useEffect(() => {
    if (notificationList) {
      setNotiCount(
        notificationList.filter((noti) => noti.isVisible === true).length
      );
    }
  }, [notificationList]);

  const handleMarkAllAsRead = async () => {
    if (notificationList) {
      for (const noti of notificationList) {
        await notiService.updateNotificationVisibilityStatus({
          newVisibilityStatus: false,
          notificationId: noti.notificationId,
        });
      }
    }

    setNotificationList([]);
    setLoading(true);
  };

  const handleMetadata = useCallback(
    async (id: number, metadata: any) => {
      if (metadata.url) {
        router.push(metadata.url);
      }
      await notiService.updateNotificationVisibilityStatus({
        newVisibilityStatus: false,
        notificationId: id,
      });

      setNotificationList([]);
      setLoading(true);
      setPanelOpen(false);
    },
    [notiService, router]
  );

  return (
    <div className="relative">
      {notiCount > 0 && (
        <div className="-top-1 -right-1 absolute size-4 bg-red-600 rounded-full text-[0.5rem] flex justify-center items-center text-center text-white">
          {notiCount}
        </div>
      )}
      <button
        ref={bellIconRef}
        onClick={togglePanel}
        className="p-2 rounded-full hover:bg-background-gray hover:cursor-pointer group"
      >
        <Bell
          className={`text-${
            notiCount > 0
              ? 'black'
              : 'global-text-secondary group-hover:text-gray-800'
          }`}
        />
      </button>
      {panelOpen && (
        <div
          ref={dropdownRef}
          className="fixed md:absolute right-0 mt-[1.3rem] md:mt-2 bg-white border border-gray-border rounded-b-xl md:rounded-xl shadow-lg z-50 text-sm font-medium transition-transform transform origin-top-right opacity-0 animate-dropdown w-screen md:w-[400px]"
        >
          <ul className="max-h-[75vh] lg:max-h-[50vh] overflow-y-scroll">
            <li className="border-b">
              <div className={`w-full p-5 flex justify-between items-center`}>
                <div className="font-semibold text-title">Notifications</div>
                <button
                  className="text-sm text-main hover:text-main-hover disabled:text-global-text-secondary"
                  onClick={handleMarkAllAsRead}
                  disabled={notiCount === 0}
                >
                  Mark all as read
                </button>
              </div>
            </li>
            {notificationList?.length === 0 && (
              <li className="border-b last:border-b-0">
                <button
                  className={`w-full p-5 flex justify-between items-center`}
                >
                  <div className="flex flex-col h-full items-start text-start">
                    <div className="text-global-text-secondary text-xs">
                      No Notifications
                    </div>
                  </div>
                </button>
              </li>
            )}
            {notificationList &&
              notificationList?.length > 0 &&
              notificationList.map((noti) => (
                <li
                  className={`border-b last:border-b-0`}
                  key={noti.notificationId}
                >
                  <button
                    className={`w-full p-5 flex justify-between items-center`}
                    onClick={() =>
                      handleMetadata(noti.notificationId, noti.metadata)
                    }
                  >
                    <div className="flex flex-col h-full items-start text-start">
                      <div className="text-global-text-secondary text-xs">
                        {moment(noti.createdAt).format('DD MMM YYYY')}
                      </div>
                      <div className="text-title text-sm font-semibold">
                        {noti.message}
                      </div>
                      <div className="text-global-text-secondary text-xs">
                        {noti.description}
                      </div>
                    </div>
                    {noti.isVisible && (
                      <div className="w-2 h-2 bg-red-600 rounded-full" />
                    )}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotiPanel;
